import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import CampusGallery from "../components/CampusGallery";
import { EditableText, EditableImage } from "../components/live/Editable";
import { Briefcase, Building2, FileDown, IndianRupee, MapPin, Users } from "lucide-react";

const COMPANIES = [
  { name: "Subros", desc: "India's largest manufacturer of automotive air conditioning" },
  { name: "IFB", desc: "Diversified company with interests in agro, automotive & home appliances sector" },
  { name: "SMIEL", desc: "Part of the Motherson Sumi Group spread across 25 countries, 180 facilities, 75,000 professionals, specializing in automotive sector" },
  { name: "GDA", desc: "Manufacturer for American medical devices industry" },
  { name: "Mikuni", desc: "Japanese MNC" },
  { name: "Koide", desc: "Japanese MNC" },
  { name: "Nagata", desc: "Japanese MNC" },
  { name: "MindaRica", desc: "Part of Rs. 3500cr Uno Minda group with operations in India, South America etc." },
];

const JOB_PROFILES = [
  "Engineer", "Junior engineer", "Shift engineer", "Industrial engineer", "Production engineer",
  "Quality engineer", "Vendor development engineer", "Automation engineer", "Maintenance engineer",
  "Applications engineer", "Tool & Die maker", "Trainee", "Entrepreneur",
];

const DEPARTMENTS = [
  "Design", "Process planning", "Tool room", "Production", "Manufacturing", "Supply chain",
  "Process improvement", "Quality", "Automation systems development", "Robotics",
];

const G = "/assets/kiki/gallery";
const PLACEMENT_MOMENTS = [
  { src: `${G}/expo-group.jpg`, caption: "Kiki students at Delhi Machine Tool Expo 2025, Bharat Mandapam, New Delhi" },
  { src: `${G}/expo-visit-1.jpg`, caption: "Industry exposure at the Delhi Machine Tool Expo, May 2025" },
  { src: `${G}/award-certificate-1.jpg`, caption: "Certificate & trophy presentation at the Annual Awards" },
  { src: `${G}/interview-panel.jpg`, caption: "Mock interview with the KIKI Careers Office" },
  { src: `${G}/expo-visit-2.jpg`, caption: "Exploring machine-tool technology at the 2025 expo" },
  { src: `${G}/award-certificate-2.jpg`, caption: "Top performers felicitated on stage" },
  { src: `${G}/assessment-lab.jpg`, caption: "Aptitude & skills assessment session" },
  { src: `${G}/award-certificate-3.jpg`, caption: "Celebrating student achievement at the Annual Awards" },
];

const BROCHURES = [
  { label: "Placement Brochure 2025", href: "https://kikicentre.com/wp-content/uploads/2026/04/Placement-Brochure-2025.pdf" },
  { label: "Placement Brochure 2024", href: "https://kikicentre.com/wp-content/uploads/2024/02/Placement-Brochure-2024.pdf" },
  { label: "Placement Brochure 2023", href: "https://kikicentre.com/wp-content/uploads/2023/03/Placement-Brochure-2019-2023.pdf" },
  { label: "Placement Brochure 2022", href: "https://kikicentre.com/wp-content/uploads/2023/03/Placement-Brochure-2018-2022.pdf" },
];

export default function PlacementPage() {
  return (
    <>
      <PageHero
        path="placement.hero"
        kicker="Student Placement"
        title="Student Placement"
        subtitle="The KIKI Careers Office helps students with their placement efforts at various companies — with a Placement Mela twice a year."
      />

      {/* Intro */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="kicker mb-3">Careers Office</p>
            <EditableText path="placement.intro.title" as="h2" className="text-3xl font-bold text-[#0B2A6B] mb-5 block">How we place our students</EditableText>
            <EditableText path="placement.intro.text1" as="p" multiline className="text-slate-700 font-semibold leading-relaxed mb-4 block" data-testid="placement-intro-text">
              For the 3.5 years diploma courses which are certified through the German Chamber of Skilled Crafts, Koblenz, Germany; the KIKI Careers Office helps students with their placement efforts at various companies. KIKI organizes a Placement Mela twice a year, once for the in-plant training programme and once for the final placement.
            </EditableText>
            <EditableText path="placement.intro.text2" as="p" multiline className="text-slate-600 leading-relaxed mb-4 block">
              Through the careers office and soft skills programme, students are taught to write their own CVs, given mock interviews and taught job search techniques.
            </EditableText>
            <p className="text-slate-600 leading-relaxed mb-4 flex items-start gap-2">
              <MapPin className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
              <EditableText path="placement.intro.locations" as="span" multiline>
                Job locations for in-plant training positions and final placements include locations such as Gurgaon, Noida, Mohali, Neemrana, Bangalore etc.
              </EditableText>
            </p>
            <EditableText path="placement.intro.text3" as="p" multiline className="text-slate-600 leading-relaxed block">
              Also, for some short term courses, where students are admitted in the Alumni category, KIKI helps students with their placement efforts.
            </EditableText>
          </div>
          <div className="space-y-6">
            <EditableImage
              path="placement.main.image"
              src="/assets/kiki/placement-1.jpg"
              alt="Student placement at Kiki Centre"
              className="rounded-3xl shadow-xl overflow-hidden"
              imgClassName="w-full h-full object-cover"
              data-testid="placement-main-image"
            />
            <div className="rounded-2xl bg-[#0B2A6B] text-white p-7 flex items-center gap-5">
              <div className="h-14 w-14 rounded-xl bg-yellow-400 flex items-center justify-center shrink-0">
                <IndianRupee className="h-7 w-7 text-[#0B2A6B]" />
              </div>
              <div>
                <EditableText path="placement.salary.label" as="p" className="text-sm text-slate-300 uppercase tracking-wider font-semibold block">Estimated Salary Range on Course Completion</EditableText>
                <EditableText path="placement.salary.value" as="p" className="text-2xl font-bold text-yellow-400 block" data-testid="placement-salary-range">₹2,40,000 – ₹5,60,000</EditableText>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brochures */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {BROCHURES.map((b) => (
              <a
                key={b.label}
                href={b.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-200 font-semibold text-[#0B2A6B] shadow-sm hover:border-yellow-400 hover:shadow-md transition"
                data-testid={`placement-brochure-${b.label.slice(-4)}`}
              >
                <FileDown className="h-4 w-4 text-yellow-600" /> {b.label}
              </a>
            ))}
            <Link
              to="/courses/earn-while-you-learn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-yellow-400 font-bold text-[#0B2A6B] shadow-sm hover:bg-yellow-500 transition"
              data-testid="placement-ewyl-link"
            >
              Student Internship – Earn While You Learn
            </Link>
          </div>
        </div>
      </section>

      {/* Placement companies */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="kicker mb-3">Our recruiters</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">Some of Our Placement Companies</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {COMPANIES.map((c, i) => (
              <div
                key={c.name}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.06}s` }}
                data-testid={`placement-company-${i}`}
              >
                <div className="h-11 w-11 rounded-xl bg-yellow-400/15 flex items-center justify-center mb-3">
                  <Building2 className="h-6 w-6 text-yellow-600" />
                </div>
                <EditableText path={`placement.company.${i}.name`} as="h3" className="font-bold text-[#0B2A6B] text-lg mb-1.5 block">{c.name}</EditableText>
                <EditableText path={`placement.company.${i}.desc`} as="p" multiline className="text-sm text-slate-600 leading-relaxed block">{c.desc}</EditableText>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job profiles + departments */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-xl bg-[#0B2A6B] flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#0B2A6B]">Job Profiles</h2>
            </div>
            <div className="flex flex-wrap gap-2.5" data-testid="placement-job-profiles">
              {JOB_PROFILES.map((j, i) => (
                <EditableText key={j} path={`placement.jobProfile.${i}`} as="span" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-yellow-400 hover:bg-yellow-50 transition">
                  {j}
                </EditableText>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-xl bg-[#0B2A6B] flex items-center justify-center">
                <Users className="h-6 w-6 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#0B2A6B]">Relevant Company Departments for Job Roles</h2>
            </div>
            <div className="flex flex-wrap gap-2.5" data-testid="placement-departments">
              {DEPARTMENTS.map((d, i) => (
                <EditableText key={d} path={`placement.department.${i}`} as="span" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-yellow-400 hover:bg-yellow-50 transition">
                  {d}
                </EditableText>
              ))}
            </div>
            <EditableImage
              path="placement.secondary.image"
              src="/assets/kiki/placement-2.jpg"
              alt="Kiki Centre students at work"
              className="rounded-2xl shadow-lg overflow-hidden mt-8"
              imgClassName="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Moments gallery */}
      <CampusGallery
        kicker="Placement & Events"
        title="Moments from campus & industry"
        subtitle="From national machine-tool expos to placement melas and award ceremonies — our students engage with the industry from day one."
        images={PLACEMENT_MOMENTS}
        bg="light"
        idPrefix="placement-gallery"
      />

      {/* CTA */}
      <section className="py-16 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <EditableText path="placement.cta.title" as="h2" className="text-3xl font-bold text-[#0B2A6B] mb-4 block">Your career starts here</EditableText>
          <EditableText path="placement.cta.text" as="p" className="text-slate-600 mb-7 block">Join a course backed by a Careers Office that works for you — from CV to final offer.</EditableText>
          <Link to="/admissions" className="inline-flex items-center px-7 py-3 rounded-lg btn-primary" data-testid="placement-apply-btn">
            Apply for Admission
          </Link>
        </div>
      </section>
    </>
  );
}
